import { Skeleton, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { nearbyBreakdownRows } from "./nearby-breakdown-rows.tsx"
import {
  NEARBY_RADIUS_MI,
  type UseNearbyFeaturesResult
} from "./useNearbyFeatures.ts"

const { Text, Title } = Typography

interface NearbyBreakdownSummaryProps {
  status: UseNearbyFeaturesResult["status"]
  counts: UseNearbyFeaturesResult["counts"]
}

/**
 * The counts-only half of the Nearby tab (`QuickViewNearby`), rendered
 * persistently below the Overview fields and below the Files list — per
 * explicit product direction, this summary must stay visible whichever of
 * those two tabs is open, not only when the user has switched to the
 * dedicated Nearby tab (which keeps its own copy of this same breakdown,
 * plus the filterable row list this summary deliberately omits). Both read
 * from the one `useNearbyFeatures` call `EntityQuickView` makes, so this
 * never triggers a second fetch of its own.
 *
 * `mt-auto` pushes this to the bottom of its scroll container (which
 * `EntityQuickView` makes a flex column for exactly this) so it always
 * lands immediately above the Open Project/View on Map footer rather than
 * trailing right under a short Overview/Files body with a gap beneath it —
 * on a tall body that overflows the scroll area, `mt-auto` has no room to
 * act and this simply follows the content, still directly above the
 * footer. `border-t` is a plain 1px solid rule (Tailwind's default border
 * style/width — never bolded), matching the footer's own separator.
 */
const NearbyBreakdownSummary = ({
  status,
  counts
}: NearbyBreakdownSummaryProps) => {
  const { t } = useTranslation()

  if (status === "loading") {
    return (
      <div className="mt-auto border-t border-gray-200 pt-3">
        <Skeleton active title={{ width: 80 }} paragraph={{ rows: 2 }} />
      </div>
    )
  }

  if (!counts) return null

  const totalCount =
    counts.project + counts.lead + counts.subcontractor + counts.appointment
  const breakdownRows = nearbyBreakdownRows(t)

  return (
    <div
      className="mt-auto flex flex-col gap-2 border-t border-gray-200 pt-3"
      data-testid="quick-view-nearby-summary"
    >
      <Title level={5} className="!mb-0 font-semibold">
        {t("globalMap.quickView.tabs.nearby")}
      </Title>
      <Text type="secondary" className="text-xs">
        {t("globalMap.quickView.nearby.recordsWithinRadius", {
          count: totalCount,
          miles: NEARBY_RADIUS_MI
        })}
      </Text>
      <ul className="m-0 flex list-none flex-col divide-y divide-gray-100 rounded-lg border border-gray-100 p-0">
        {breakdownRows.map((row) => (
          <li
            key={row.key}
            className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
          >
            <span className="flex items-center gap-2 text-slate-700">
              <span
                aria-hidden="true"
                className="flex size-5 shrink-0 items-center justify-center text-base"
                style={{ color: row.color }}
              >
                {row.icon}
              </span>
              {row.label}
            </span>
            <span className="font-medium text-slate-900">
              {counts[row.key]}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export { NearbyBreakdownSummary }
